import appSchema from '../../../../appSchema.js';
import { appModelOptions } from '../../../../appModelOptions.js';
import { webhookTriggerModeOptions } from '../../../../webhookTriggerModeSchema.js';

const authOptions = appSchema.authentication.types.map((type) => ({
  label: type.label,
  value: type.type
}));

const apiOptions = appSchema.api.types.map((type) => ({
  label: type.label,
  value: type.type
}));

const defaultAuthOptions = (availableAuths) =>
  availableAuths?.map((auth) => ({
    label: auth.name || appSchema.authentication.types.find((t) => t.type === auth.type)?.label,
    value: auth.id
  })) || [];

const homePropertiesSchema = ({ availableAuths }) => {
  return {
    ai: {
      action: 'configureApp',
      placeholder: 'Tell AI how to configure the app properties',
      baseSuggestion: 'Tell AI to',
      baseDescription: 'You can ask AI to configure the app properties from here'
    },
    fields: [
      {
        key: 'name',
        name: 'App Name',
        description: 'The name of the app.',
        path: 'name',
        dataType: 'string'
      },
      {
        key: 'slug',
        name: 'App Slug',
        description: 'The slug of the app.',
        path: 'slug',
        dataType: 'string'
      },
      {
        key: 'description',
        name: 'App Description',
        description: 'The description of the app.',
        path: 'description',
        dataType: 'string'
      },
      {
        key: 'type',
        name: 'App Type',
        description: 'The type of the app.',
        path: 'type',
        dataType: 'list',
        options: appSchema.general.types
      },
      {
        key: 'category',
        name: 'App Category',
        description: 'The category of the app.',
        path: 'category',
        dataType: 'list',
        options: appSchema.general.categories
      },
      {
        key: 'hidden',
        name: 'Hidden',
        description: 'Whether the app is hidden from the app list or not.',
        path: 'hidden',
        dataType: 'boolean'
      },
      {
        key: 'whitelistedCompanies',
        name: 'Whitelisted Companies',
        description: 'The companies that can use this app when it is hidden.',
        path: 'whitelistedCompanies',
        dataType: 'array',
        itemDataType: 'string',
        parentKey: 'hidden',
        visible: (result) => result?.hidden === true
      },
      {
        key: 'authType',
        name: 'Authentication Type',
        description: 'The type of authentication to use for this app.',
        path: 'authType',
        dataType: 'list',
        options: authOptions
      },
      {
        key: 'apiType',
        name: 'API Type',
        description: 'The type of API to use for this app.',
        path: 'apiType',
        dataType: 'list',
        options: apiOptions
      },
      {
        key: 'defaultAuthId',
        name: 'Default Authentication',
        description: 'The default authentication method to use for this app.',
        path: 'defaultAuthId',
        dataType: 'list',
        options: defaultAuthOptions(availableAuths)
      },
      {
        key: 'modelClass',
        name: 'Application Class',
        description: 'What kind of application this is (for example CRM, ERP, or developer tools).',
        path: 'modelClass',
        dataType: 'list',
        options: appModelOptions
      },
      {
        key: 'showOnCirra',
        name: 'Show on Cirra',
        description: 'Whether the app is exposed in Cirra surfaces, including the Apps MCP catalog and connection picker.',
        path: 'showOnCirra',
        dataType: 'boolean'
      },
      {
        key: 'enableActionGrouping',
        name: 'Enable Action Grouping',
        description: 'Enable this if you want to group actions.',
        path: 'enableActionGrouping',
        dataType: 'boolean'
      },
      {
        key: 'helpDocumentId',
        name: 'Help Document ID',
        description: 'ID that links to documentation explaining how to use this app.',
        path: 'helpDocumentId',
        dataType: 'string'
      },
      {
        key: 'aliases',
        name: 'App Aliases',
        description: 'Add different names for this app to make it easier to search for it.',
        path: 'aliases',
        dataType: 'array',
        itemDataType: 'string'
      },
      {
        key: 'enableTriggers',
        name: 'Enable Triggers',
        description: 'Enable this if you want to add trigger actions.',
        path: 'enableTriggers',
        dataType: 'boolean'
      },
      {
        key: 'triggerMode',
        name: 'Trigger Mode',
        description: 'The mode for your triggers.',
        path: 'triggerMode',
        dataType: 'list',
        options: webhookTriggerModeOptions,
        parentKey: 'enableTriggers',
        visible: (result) => result?.enableTriggers === true
      },
      {
        key: 'component',
        name: 'Component',
        description: 'If this is special app that has its own React Component, this is the component name. Otherwise, leave empty.',
        path: 'component',
        dataType: 'string'
      },
      {
        key: 'deployComponent',
        name: 'Deploy Component',
        description: 'If this has a special app deploy step, this is the React component name to load in the deploy tab. Otherwise, leave empty.',
        path: 'deployComponent',
        dataType: 'string'
      }
    ]
  };
};

export default homePropertiesSchema;
